// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Ban, Search } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getSessionUser } from '@/lib/authSession';

type Filter = "all" | "active" | "expired" | "used_up";

interface Coupon {
  id: string;
  code: string;
  description: string | null;
  discount_type: "percent" | "fixed";
  discount_value: number;
  scope: string;
  scoped_event_ids: string[] | null;
  scoped_user_id: string | null;
  scoped_email: string | null;
  max_uses: number | null;
  max_uses_per_user: number | null;
  used_count: number;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
}

const EMPTY_COUPON: Partial<Coupon> = {
  code: "",
  description: "",
  discount_type: "percent",
  discount_value: 0,
  scope: "all_events",
  scoped_event_ids: [],
  scoped_user_id: null,
  scoped_email: null,
  max_uses: null,
  max_uses_per_user: 1,
  valid_from: null,
  valid_until: null,
  is_active: true,
};

export default function CouponsPage() {
  const { scopeOrgId, createOrgId, isSuperAdmin, ready } = useAdminOrgScope();
  const orgId = createOrgId;

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<Coupon> | null>(null);

  const load = async () => {
    if (!ready) return;
    setLoading(true);
    // Org admin -> only their coupons/events; super admin -> all.
    let couponsQuery = supabase
      .from("event_coupons_v2")
      .select("*")
      .order("created_at", { ascending: false });
    let eventsQuery = supabase.from("events").select("id, name").order("name");
    if (scopeOrgId) {
      couponsQuery = couponsQuery.eq("organization_id", scopeOrgId);
      eventsQuery = eventsQuery.eq("organization_id", scopeOrgId);
    }
    const [{ data: c }, { data: e }] = await Promise.all([couponsQuery, eventsQuery]);
    setCoupons(c ?? []);
    setEvents(e ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [ready, scopeOrgId]);

  const isExpired = (c: Coupon) => !!c.valid_until && new Date(c.valid_until) < new Date();
  const isUsedUp = (c: Coupon) => !!c.max_uses && c.used_count >= c.max_uses;
  const statusOf = (c: Coupon) => {
    if (!c.is_active) return "disabled";
    if (isExpired(c)) return "expired";
    if (isUsedUp(c)) return "used_up";
    return "active";
  };

  const filtered = useMemo(() => {
    return coupons.filter((c) => {
      const status = statusOf(c);
      if (filter === "active" && status !== "active") return false;
      if (filter === "expired" && status !== "expired") return false;
      if (filter === "used_up" && status !== "used_up") return false;
      if (search && !c.code.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [coupons, filter, search]);

  const save = async () => {
    if (!editing || !orgId) return;
    if (!editing.code?.trim()) {
      toast.error("Code is required");
      return;
    }
    const payload = {
      organization_id: orgId,
      code: editing.code.trim().toUpperCase().replace(/\s+/g, ""),
      description: editing.description || null,
      discount_type: editing.discount_type,
      discount_value: editing.discount_value || 0,
      scope: editing.scope || "all_events",
      scoped_event_ids:
        editing.scope === "specific_events" ? editing.scoped_event_ids ?? [] : null,
      scoped_user_id: editing.scoped_user_id || null,
      scoped_email: editing.scoped_email || null,
      max_uses: editing.max_uses ?? null,
      max_uses_per_user: editing.max_uses_per_user ?? null,
      valid_from: editing.valid_from || null,
      valid_until: editing.valid_until || null,
      is_active: editing.is_active ?? true,
    };
    let error;
    if (editing.id) {
      ({ error } = await supabase.from("event_coupons_v2").update(payload).eq("id", editing.id));
    } else {
      const { data: u } = await getSessionUser();
      ({ error } = await supabase
        .from("event_coupons_v2")
        .insert({ ...payload, created_by: u?.user?.id ?? null }));
    }
    if (error) {
      toast.error("Failed to save: " + error.message);
      return;
    }
    toast.success("Coupon saved");
    setEditing(null);
    load();
  };

  const toggleDisable = async (c: Coupon) => {
    await supabase.from("event_coupons_v2").update({ is_active: !c.is_active }).eq("id", c.id);
    load();
  };

  const remove = async (c: Coupon) => {
    if (!confirm(`Delete coupon "${c.code}"? This cannot be undone.`)) return;
    const { error } = await supabase.from("event_coupons_v2").delete().eq("id", c.id);
    if (error) toast.error("Failed to delete: " + error.message);
    else {
      toast.success("Deleted");
      load();
    }
  };

  const STATUS_STYLES: Record<string, string> = {
    active: "bg-green-100 text-green-800",
    expired: "bg-gray-100 text-gray-700",
    used_up: "bg-gray-100 text-gray-700",
    disabled: "bg-red-100 text-red-700",
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Coupons</h1>
          <p className="text-sm text-muted-foreground">
            Discount codes that can be used at registration.
          </p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY_COUPON })}>
          <Plus className="w-4 h-4 mr-1" /> Add Coupon
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {(["all", "active", "expired", "used_up"] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              "px-3 py-1 text-xs rounded-full border capitalize",
              filter === f
                ? "bg-primary text-white border-primary"
                : "bg-background border-border hover:border-primary"
            )}
          >
            {f.replace("_", " ")}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-64"
          />
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left">Discount</th>
              <th className="px-3 py-2 text-left">Scope</th>
              <th className="px-3 py-2 text-left">Used / Max</th>
              <th className="px-3 py-2 text-left">Valid Until</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr>
                <td colSpan={8} className="p-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-6 text-center text-muted-foreground">
                  No coupons found.
                </td>
              </tr>
            ) : (
              filtered.map((c) => {
                const status = statusOf(c);
                const scopeLabel =
                  c.scope === "all_events"
                    ? "All events"
                    : c.scope === "specific_events"
                    ? `${c.scoped_event_ids?.length ?? 0} events`
                    : c.scoped_email
                    ? `User: ${c.scoped_email}`
                    : c.scope;
                return (
                  <tr key={c.id}>
                    <td className="px-3 py-2 font-mono font-semibold">{c.code}</td>
                    <td className="px-3 py-2">{c.description || "—"}</td>
                    <td className="px-3 py-2">
                      {c.discount_type === "percent"
                        ? `${c.discount_value}% off`
                        : `$${c.discount_value} off`}
                    </td>
                    <td className="px-3 py-2">{scopeLabel}</td>
                    <td className="px-3 py-2">
                      {c.used_count} / {c.max_uses ?? "∞"}
                    </td>
                    <td className="px-3 py-2">
                      {c.valid_until ? new Date(c.valid_until).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={cn("px-2 py-0.5 rounded text-xs capitalize", STATUS_STYLES[status])}
                      >
                        {status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setEditing(c)}
                          className="p-1 hover:bg-muted rounded"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleDisable(c)}
                          className="p-1 hover:bg-muted rounded"
                          title={c.is_active ? "Disable" : "Enable"}
                        >
                          <Ban className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(c)}
                          className="p-1 hover:bg-red-50 text-red-700 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <CouponDialog
        coupon={editing}
        events={events}
        onClose={() => setEditing(null)}
        onChange={setEditing}
        onSave={save}
      />
    </div>
  );
}

function CouponDialog({
  coupon,
  events,
  onClose,
  onChange,
  onSave,
}: {
  coupon: Partial<Coupon> | null;
  events: any[];
  onClose: () => void;
  onChange: (c: Partial<Coupon>) => void;
  onSave: () => void;
}) {
  if (!coupon) return null;
  const restrictUser = !!coupon.scoped_email || !!coupon.scoped_user_id;
  return (
    <Dialog open={!!coupon} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{coupon.id ? "Edit Coupon" : "Add Coupon"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Code *</label>
              <Input
                value={coupon.code ?? ""}
                onChange={(e) =>
                  onChange({ ...coupon, code: e.target.value.toUpperCase().replace(/\s+/g, "") })
                }
                placeholder="EARLYBIRD2026"
              />
            </div>
            <div>
              <label className="text-xs font-medium">Description</label>
              <Input
                value={coupon.description ?? ""}
                onChange={(e) => onChange({ ...coupon, description: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium block mb-1">Discount Type</label>
              <div className="flex gap-3 text-sm">
                {(["percent", "fixed"] as const).map((t) => (
                  <label key={t} className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={coupon.discount_type === t}
                      onChange={() => onChange({ ...coupon, discount_type: t })}
                    />
                    <span className="capitalize">{t === "fixed" ? "Fixed amount" : t}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">Discount Value *</label>
              <div className="relative">
                <Input
                  type="number"
                  value={coupon.discount_value ?? 0}
                  onChange={(e) =>
                    onChange({ ...coupon, discount_value: parseFloat(e.target.value) || 0 })
                  }
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {coupon.discount_type === "percent" ? "%" : "$"}
                </span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">Scope</label>
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={coupon.scope === "all_events"}
                  onChange={() => onChange({ ...coupon, scope: "all_events" })}
                />
                All events in organization
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={coupon.scope === "specific_events"}
                  onChange={() => onChange({ ...coupon, scope: "specific_events" })}
                />
                Specific events
              </label>
              {coupon.scope === "specific_events" && (
                <select
                  multiple
                  value={coupon.scoped_event_ids ?? []}
                  onChange={(e) =>
                    onChange({
                      ...coupon,
                      scoped_event_ids: Array.from(e.target.selectedOptions).map((o) => o.value),
                    })
                  }
                  className="w-full border border-input rounded p-2 text-sm h-32"
                >
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>
                      {ev.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="border-t pt-3 space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={restrictUser}
                onChange={(e) =>
                  onChange({
                    ...coupon,
                    scoped_email: e.target.checked ? coupon.scoped_email ?? "" : null,
                    scoped_user_id: e.target.checked ? coupon.scoped_user_id : null,
                  })
                }
              />
              Restrict to one user
            </label>
            {restrictUser && (
              <Input
                placeholder="user@example.com"
                value={coupon.scoped_email ?? ""}
                onChange={(e) => onChange({ ...coupon, scoped_email: e.target.value })}
              />
            )}
            {/* TODO: autocomplete from auth.users via an edge function — for now plain email */}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Max total uses</label>
              <Input
                type="number"
                placeholder="Unlimited"
                value={coupon.max_uses ?? ""}
                onChange={(e) =>
                  onChange({
                    ...coupon,
                    max_uses: e.target.value ? parseInt(e.target.value) : null,
                  })
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium">Max uses per user</label>
              <Input
                type="number"
                value={coupon.max_uses_per_user ?? 1}
                onChange={(e) =>
                  onChange({
                    ...coupon,
                    max_uses_per_user: e.target.value ? parseInt(e.target.value) : null,
                  })
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Valid from</label>
              <Input
                type="datetime-local"
                value={coupon.valid_from?.slice(0, 16) ?? ""}
                onChange={(e) =>
                  onChange({ ...coupon, valid_from: e.target.value ? new Date(e.target.value).toISOString() : null })
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium">Valid until</label>
              <Input
                type="datetime-local"
                value={coupon.valid_until?.slice(0, 16) ?? ""}
                onChange={(e) =>
                  onChange({ ...coupon, valid_until: e.target.value ? new Date(e.target.value).toISOString() : null })
                }
              />
            </div>
          </div>

          <label className="flex items-center justify-between p-3 border border-border rounded-lg">
            <span className="text-sm font-medium">Active</span>
            <input
              type="checkbox"
              checked={coupon.is_active ?? true}
              onChange={(e) => onChange({ ...coupon, is_active: e.target.checked })}
            />
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
